// ✅ src/pages/OrderManagement/Reconciliation.jsx
// Template style (no ThemeContext)
// Fixes:
// ✅ No page-level horizontal scrollbar (overflow-x-hidden)
// ✅ Scrollbar only inside table container (overflow-x-auto + max-h + overflow-y-auto)
// ✅ Green "Back to Top" arrow button
// ✅ localStorage headers added

import React, { useEffect, useMemo, useState } from "react";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaSyncAlt,
  FaPrint,
  FaTable,
  FaArrowUp,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ CHANGE THIS to your actual backend mount if different
const API = "http://nissi-india.com/api/reconciliation";

const toInt = (v, def = 0) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : def;
};

const toNum = (v, def = 0) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : def;
};

export default function Reconciliation() {
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ✅ Safe localStorage helper
  const getLS = (keys, fallback = "") => {
    if (typeof window === "undefined") return fallback;

    const keyList = Array.isArray(keys) ? keys : [keys];

    for (const key of keyList) {
      const val = localStorage.getItem(key);
      if (val !== null && val !== undefined && String(val).trim() !== "") {
        return String(val).trim();
      }
    }

    return fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS(["finYearId"]);
  const finYear = getLS(["finYear"]);
  const branchIdLS = getLS(["branchId", "branchid"]);
  const branchTypeLS = getLS(["branchType", "branchtype"]);
  const userIdLS = getLS(["userId", "loginuserid", "loginUserId"]);

  // ✅ parsed localStorage values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const loginUserId = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId || "",
      "x-finyear": finYear || "",
      "x-branchid": String(branchId || 0),
      "x-branchtype": branchType || "",
      "x-userid": String(loginUserId || 0),
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  // ✅ contractId from URL or session
  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";
    return toInt(fromUrl || fromSession || 0);
  }, [location.search]);

  useEffect(() => {
    if (contractId) {
      sessionStorage.setItem("Selcontractid", String(contractId));
    }
  }, [contractId]);

  const sidebarItems = [
    { name: "Date/LOA", icon: FaCalendarAlt, path: "/OrderManagement/ProjectEntry" },
    { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: "/OrderManagement/BOQ" },
    { name: "Program/DPR", icon: FaChartBar, path: "/OrderManagement/DailyProgressMonthlyWise" },
    { name: "Purchase Order", icon: FaFileAlt, path: "/OrderManagement/PurchaseOrderListing" },
    { name: "Rate Approval", icon: FaCheckCircle, path: "/OrderManagement/RateApprove" },
    { name: "Risk/Issues", icon: FaExclamationTriangle, path: "/OrderManagement/RiskAndIssue" },
    { name: "Documentation", icon: FaFileAlt, path: "/OrderManagement/ProjectEntryDocumentation" },
    { name: "Photos", icon: FaBuilding, path: "/OrderManagement/PhotoDocument" },
    { name: "Reconciliation", icon: FaTasks, path: "/OrderManagement/Reconciliation", active: true },
    { name: "Do/Don't's", icon: FaWarehouse, path: "/OrderManagement/Rules" },
    { name: "Vendor Details", icon: FaUsers, path: "/OrderManagement/ProjectVendorDetails" },
  ];

  // =========================
  // Data
  // =========================
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // ✅ Green up arrow show/hide
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 250);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const loadData = async () => {
    if (!contractId) {
      setErr("contractId missing in URL");
      toast.error("contractId missing in URL");
      return;
    }

    setLoading(true);
    setErr("");

    try {
      const res = await fetch(`${API}/view?contractId=${contractId}`, {
        method: "GET",
        headers: {
          ...commonHeaders,
        },
      });

      const data = await res.json();

      if (!data?.success) {
        setErr(data?.message || "Failed to load reconciliation");
        toast.error(data?.message || "Failed to load reconciliation");
        return;
      }

      const list = data?.rows || data?.data || data?.result || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Reconciliation load error:", e);
      setErr(e?.message || "Server error");
      toast.error(e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, commonHeaders]);

  const onPrint = () => {
    if (!contractId) {
      toast.error("contractId missing");
      return;
    }

    window.open(
      `/OrderManagement/ReconciliationPrint?contractId=${contractId}`,
      "_blank",
      "width=900,height=900,top=(screen.height-900)/2,left=(screen.width-900)/2,resizable=no,scrollbars=yes,toolbar=no,status=no"
    );
  };

  return (
    <div
      className={`min-h-screen overflow-x-hidden transition-all duration-300 ${
        isDark ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      <ToastContainer position="top-right" autoClose={2500} />

      {/* Top Bar */}
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>

              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </h1>
              </div>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3">
              <button
                disabled={loading}
                onClick={loadData}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                  loading
                    ? "bg-blue-400 text-white cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                <FaSyncAlt />
                Refresh
              </button>

              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                    : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? "w-64" : "w-16"
          } transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={`${item.path}?contractId=${contractId || ""}`}
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  window.location.pathname === item.path
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        {/* Main */}
        <div className="flex-1">
          <div
            className={`transition-all duration-300 ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            {/* Header */}
            <div
              className={`backdrop-blur-md border-b ${
                isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1
                      className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <FaTable className="text-blue-500" />
                      RECONCILIATION
                    </h1>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      ContractId: <b>{contractId || "-"}</b> • BOQ vs Actual Consumed Qty
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={onPrint}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaPrint />
                      Print
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* LIST CARD */}
              <div
                className={`rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
                  <h2 className="text-lg font-bold">RECONCILIATION LIST</h2>
                </div>

                <div className="p-6">
                  {err ? (
                    <div
                      className={`p-4 rounded-lg border ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-gray-200"
                          : "bg-red-50 border-red-200 text-red-700"
                      }`}
                    >
                      {err}
                    </div>
                  ) : null}

                  <div
                    className={`mt-4 rounded-xl border ${
                      isDark ? "border-gray-700 bg-gray-900/10" : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
                      <table className="w-full text-sm min-w-[1100px]">
                        <thead className={`${isDark ? "bg-gray-900/60" : "bg-gray-50"} sticky top-0 z-10`}>
                          <tr className={`${isDark ? "text-gray-200" : "text-gray-700"} border-b`}>
                            <th className="px-4 py-3 text-left w-[70px]">S.No</th>
                            <th className="px-4 py-3 text-left w-[220px]">Category</th>
                            <th className="px-4 py-3 text-left">Nature Of Work</th>
                            <th className="px-4 py-3 text-left w-[110px]">Unit</th>
                            <th className="px-4 py-3 text-center w-[120px]">
                              <span className="text-red-600 font-bold">Billed Qty</span>
                            </th>
                            <th className="px-4 py-3 text-center w-[160px]">
                              <span className="text-blue-600 font-bold">Actual Consumed Qty</span>
                            </th>
                            <th className="px-4 py-3 text-center w-[140px]">Difference (%)</th>
                          </tr>
                        </thead>

                        <tbody>
                          {loading ? (
                            <tr>
                              <td
                                colSpan={7}
                                className={`px-4 py-8 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}
                              >
                                Loading...
                              </td>
                            </tr>
                          ) : rows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className={`px-4 py-8 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}
                              >
                                No Record Found
                              </td>
                            </tr>
                          ) : (
                            rows.map((r, idx) => {
                              const category =
                                r.fld_Projectcategoryname ??
                                r.projectCategoryName ??
                                "";
                              const work = r.fld_work ?? r.work ?? "";
                              const unit = r.fld_unit ?? r.unit ?? "";
                              const billedQty = r.fld_qty ?? r.billedQty ?? "";
                              const actualQty = r.fld_opQty ?? r.actualQty ?? "";
                              const diffRaw = r.fld_difference ?? r.difference ?? "";
                              const diff = diffRaw === "" ? "" : toNum(diffRaw, 0);

                              return (
                                <tr
                                  key={idx}
                                  className={`border-b ${
                                    isDark
                                      ? "border-gray-700 text-gray-200"
                                      : "border-gray-200 text-gray-800"
                                  }`}
                                >
                                  <td className="px-4 py-4">{idx + 1}</td>
                                  <td className="px-4 py-4">{category}</td>
                                  <td className="px-4 py-4 leading-relaxed">{work}</td>
                                  <td className="px-4 py-4">{unit}</td>
                                  <td className="px-4 py-4 text-center text-red-600 font-semibold">
                                    {billedQty}
                                  </td>
                                  <td className="px-4 py-4 text-center text-blue-600 font-semibold">
                                    {actualQty}
                                  </td>
                                  <td className="px-4 py-4 text-center font-semibold">
                                    {diffRaw === "" ? "" : `${diff}%`}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className={`mt-3 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Tip: Scroll inside the table to view all columns/rows.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Green Up Arrow */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-50 bg-green-600 hover:bg-green-700 text-white rounded-full w-12 h-12 shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
          title="Back to top"
          type="button"
        >
          <FaArrowUp />
        </button>
      )}
    </div>
  );
}